import { type RefObject, useLayoutEffect, useRef, useState } from "react";

interface UseAvailableHeightReturn<T extends HTMLElement> {
  ref: RefObject<T | null>;
  maxHeight: number | undefined;
}

/**
 * Measures the distance from an element's top edge to the viewport bottom,
 * returning it as `maxHeight`. Pair with `overflow-hidden` to fill exactly
 * the remaining viewport space without causing scroll.
 *
 * Uses `useLayoutEffect` so the measurement completes before the first paint.
 */
export function useAvailableHeight<T extends HTMLElement = HTMLDivElement>(): UseAvailableHeightReturn<T> {
  const ref = useRef<T>(null);
  const [maxHeight, setMaxHeight] = useState<number | undefined>();

  useLayoutEffect(() => {
    if (ref.current) {
      setMaxHeight(window.innerHeight - ref.current.getBoundingClientRect().top);
    }
  }, []);

  return { ref, maxHeight };
}
